<template>
    <div class="quick-search d-flex justify-content-between align-items-center gap-16 p-16 inset-border-bottom">
        <div class="flex-grow-1 mr-4">
            <Input
                id="input-search"
                ref="searchInput"
                v-model="search"
                v-focus
                label=""
                leadingIcon="far fa-search fa-xs"
                type="text">
                <ButtonIcon
                    v-show="search.length > 0"
                    ariaLabel="Remove"
                    class="no-padding-button"
                    icon="far fa-times"
                    variant="borderless"
                    style="border-width: 0 !important; position: relative; top: -8px"
                    @click="search = ['', 0]; focusSearch()"/>
            </Input>
        </div>
        <div>
            <Button variant="quiet" text="Cancel" @click="handleClick"/>
        </div>
    </div>
    <div v-if="search.length > 0 || previousSearches.size > 0" class="quick-search d-flex justify-content-between align-items-center p-16 inset-border-bottom">
        <template v-if="search.length > 0">
            <span class="tm16 text-midnight-800">Search Results</span>
            <span class="tr16 text-midnight-600">{{ searchResults.length }} item{{ searchResults.length !== 1 ? 's' : '' }}</span>
        </template>
        <template v-else>
            <span class="tm16 text-midnight-800">Recent searches</span>
            <Button variant="quiet" text="Clear all" @click="previousSearches = new Map<string, IProduct[]>([])"/>
        </template>
    </div>
    <div v-if="!(search.length > 0) && previousSearches.size > 0" class="d-flex flex-column justify-content-between align-items-center">
        <span v-for="([entry,], key) in previousSearches" :key="key" class="d-flex justify-content-between align-items-center w-100 gap-16 p-16" @click="searchEntry(entry)">
            <span class="tm16 text-midnight-800">{{ entry }}</span>
            <ButtonIcon ariaLabel="Remove" class="no-padding-button" icon="far fa-times" variant="borderless" @click.stop="removeEntry(entry)"/>
        </span>
    </div>
</template>
<script setup lang="ts">
import {Input, Button, ButtonIcon} from '@helcim/blizzard';
import {computed, Directive, ref, watch} from 'vue';
import {useDebounce} from '../../compositions';
import {useProductStore} from '../../store/products';
import {IProduct} from '../../types';

const { dynamicDebounceRef } = useDebounce();
const productStore = useProductStore();

const search = dynamicDebounceRef(['', 750]);

const searchResults = ref<IProduct[]>([]);

const previousSearches = ref(new Map<string, IProduct[]>([]));

const searchInput = ref<InstanceType<typeof Input>>(null);

const focusSearch = () => searchInput.value.inputRef.focus();

const vFocus: Directive = {
    mounted: focusSearch,
};

const emit = defineEmits<{
    (event: 'toggle-search'): void,
}>();

const searchEntry = (entry: string) => {
    search.value = [entry, 0];
};

const removeEntry = (entry: string) => {
    previousSearches.value.delete(entry);
};

watch(search, (v: string) => {
    if(!v) {
        return;
    }
    if(previousSearches.value.has(v)) {
        searchResults.value = previousSearches.value.get(v);
        return;
    }
    const products = productStore.productList.filter(product => {
        const token = v.toLowerCase();
        return product.name.toLowerCase().includes(token) || product.description.toLowerCase().includes(token);
    });
    if(products.length > 0) {
        previousSearches.value.set(v, products);
    }
    searchResults.value = products;
});

const handleClick = () => {
    emit('toggle-search');
};

const isSearchEmpty = computed(() => !search.value.length);

defineExpose({
    searchResults,
    isSearchEmpty,
    focusSearch,
});
</script>
<style lang="scss">
.quick-search {
    .hds-m-input {
        input {
            height: 2.5rem;
            border-radius: 1.25rem;
            padding-top: 0.5rem;
            padding-bottom: 0.5rem;
            padding-left: calc(3rem - 1px);
        }
    }
    .leading-icon {
        left: 0.5rem !important;
        top: 1.25rem !important;
    }
    .hds-a-button {
        padding: 0 !important;
        border: none !important;
    }
}
.inset-border-bottom {
  box-shadow: inset 0px -1px 0px #D8DAE1;
}

.no-padding-button {
  button {
    height: 1.25rem !important;
    width: 1.25rem !important;
  }
}
</style>